import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import { invalid } from '../validation.ts';
import {
  errorText,
  formatError,
  result,
  stableJson,
  validation,
  validationError,
} from './shared.ts';

function context2d(): CanvasRenderingContext2D {
  const context = document.createElement('canvas').getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D is unavailable');
  }
  return context;
}

async function stableSurface(): Promise<RawData> {
  const context = context2d();
  context.font = '16px Arial';
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screen: [screen.width, screen.height, window.devicePixelRatio],
    canvasMetric: context.measureText('ABC Привет').width,
    math: Math.acos(0.123),
  };
}

async function collect(): Promise<RawData> {
  return { reads: [await stableSurface(), await stableSurface()] };
}

function isStable(rawData: RawData): boolean {
  const reads = Array.isArray(rawData.reads) ? rawData.reads : [];
  return reads.length == 2 && stableJson(reads[0]) == stableJson(reads[1]);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return isStable(rawData) ? 'Stable' : 'Drift detected';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  if (!isStable(rawData)) {
    return invalid('Common browser surface changed between repeated reads');
  }
  return validation([]);
}

export const stabilityTest: TestDefinition = {
  id: 'fingerprintStability',
  title: 'Fingerprint stability',
  category: 'shared',
  format,
  run,
  validate,
};
